// Centralized Supabase configuration.
//
// All values derive from the configured Supabase URL (env.supabaseUrl) so that
// local / staging / production all resolve to the right backend. These were
// previously hardcoded to the production project ref, which silently broke any
// consumer that keyed off SUPABASE_STORAGE_KEY (e.g. Ruby's session pre-flight
// in auth-utils.ts) and SUPABASE_FUNCTIONS_URL (e.g. verify-phone) on every
// non-production environment.
import { env } from '@/config/env';

/** Production project ref. Used only as the fallback when no URL is configured. */
export const SUPABASE_PROJECT_ID = 'kwvlguzoxycjvlsvfzgj';

const PROD_SUPABASE_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co`;

/**
 * Derive the project ref the same way @supabase/supabase-js derives its default
 * auth storage key: the first dot-separated label of the URL hostname. For a
 * local URL (`http://127.0.0.1:54321`) this yields `127`, i.e. the client
 * persists the session under `sb-127-auth-token`. Matching this exactly is what
 * keeps session look-ups working off-production.
 */
export function projectRefFromUrl(url: string | undefined | null): string {
  if (!url) return SUPABASE_PROJECT_ID;
  try {
    return new URL(url).hostname.split('.')[0];
  } catch {
    return SUPABASE_PROJECT_ID;
  }
}

/**
 * Strip trailing slashes so URL construction never produces a double-slash
 * (e.g. `https://xyz.supabase.co//functions/v1`). Falls back to the production
 * URL when no URL is configured.
 */
export function normalizeSupabaseUrl(url: string | undefined | null): string {
  return (url || PROD_SUPABASE_URL).replace(/\/+$/, '');
}

const supabaseUrl = normalizeSupabaseUrl(env.supabaseUrl);

export const SUPABASE_BASE_URL = supabaseUrl;

export const SUPABASE_FUNCTIONS_URL = `${supabaseUrl}/functions/v1`;

// Storage key for auth tokens. MUST match the key @supabase/supabase-js derives
// from the configured URL, otherwise session look-ups silently fail off-prod.
export const SUPABASE_STORAGE_KEY = `sb-${projectRefFromUrl(supabaseUrl)}-auth-token`;
